import { useCallback, useMemo, useReducer, useRef } from "react";
import type { Dispatch, Reducer } from "react";

export interface ClearableProvider {
  clearState: () => void;
}

interface ClearableState<State> {
  state: State;
  generation: number;
}

type ClearableAction<State, Action> = { type: "reset"; state: State; generation: number } | { type: "dispatch"; action: Action };

/** Resets provider data and invalidates dispatches held by requests from the previous session. */
export function useClearableReducer<State, Action>(
  reducer: Reducer<State, Action>,
  initialState: State
): [State, Dispatch<Action>, () => void] {
  const generationRef = useRef(0);
  const [snapshot, dispatchSnapshot] = useReducer(
    (previous: ClearableState<State>, action: ClearableAction<State, Action>) => {
      if (action.type === "reset") {
        return { state: action.state, generation: action.generation };
      }
      return { ...previous, state: reducer(previous.state, action.action) };
    },
    { state: initialState, generation: 0 }
  );
  const dispatch = useMemo<Dispatch<Action>>(() => {
    const generation = snapshot.generation;
    return (action) => {
      if (generationRef.current === generation) {
        dispatchSnapshot({ type: "dispatch", action });
      }
    };
  }, [snapshot.generation]);
  const clearState = useCallback(() => {
    generationRef.current += 1;
    dispatchSnapshot({ type: "reset", state: initialState, generation: generationRef.current });
  }, [initialState]);
  return [snapshot.state, dispatch, clearState];
}
